/**
 * Página pública de precios · /pricing
 *
 * Fase 2B · Founder-fixed (Feb 2026):
 *  - EXCLUSIVAMENTE 2 planes comerciales activos:
 *      · Agenda & Gestión — 29 €/mes (`agenda_gestion`)
 *      · Ecosystem Full   — 59 €/mes (`ecosystem_full`) · recomendado
 *  - Facturación mensual únicamente (sin toggle anual)
 *  - Sin plan Enterprise / Multi-centro (retirado del scope comercial)
 *  - IVA visible en cada card (+21% no incluido)
 */
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTheme } from "../lib/theme";
import {
  Check, ArrowRight, Star, CaretDown, Tag as PriceTag, Sun, Moon, Sparkle,
} from "@phosphor-icons/react";
import { AutoGlowBrand } from "./components/AutoGlowLogo";

const PLANS = [
  {
    id: "agenda_gestion",
    name: "Agenda & Gestión",
    desc: "Lo esencial para empezar a digitalizar tu negocio sin complicarte.",
    price: 29,
    period: "/mes",
    cta: "Probar 30 días gratis",
    ctaHref: "/register",
    style: "outline",
    features: [
      "Reservas online ilimitadas",
      "Calendario y agenda profesional",
      "Marketplace público con tu ficha",
      "Facturación básica + tickets",
      "Soporte por email",
    ],
  },
  {
    id: "ecosystem_full",
    name: "Ecosystem Full",
    desc: "El paquete completo: agenda + contabilidad AEAT + equipo + IA.",
    price: 59,
    period: "/mes",
    cta: "Probar 30 días gratis",
    ctaHref: "/register?plan=ecosystem_full",
    popular: true,
    style: "gold",
    features: [
      "Todo lo del plan Agenda & Gestión",
      "Equipo · invita colaboradores y operarios",
      "Modelos AEAT automáticos (303, 130, 111, 390)",
      "Facturación con IRPF split y DIR3",
      "Justificantes de gastos + lectura OCR",
      "Marketplace destacado · ranking premium",
      "IA: predicción no-show, churn y MRR",
    ],
  },
];

const COMPARISON_ROWS = [
  ["Reservas online",                "Ilimitadas", "Ilimitadas"],
  ["Facturación AEAT (IRPF, IVA)",   "Básica",     "Completa"],
  ["Marketplace público",            "yes",        "yes"],
  ["Equipo / colaboradores",         "no",         "Ilimitado"],
  ["Modelos AEAT (303, 130, 111)",   "no",         "yes"],
  ["IA · no-show + churn + MRR",     "no",         "yes"],
  ["Soporte",                        "Email 48h",  "Prioridad 8h"],
];

const FAQS = [
  { q: "¿Necesito tarjeta de crédito para probar?",
    a: "No. Te damos 30 días gratis sin tarjeta. Si al final del trial decides no continuar, simplemente no se cobra nada y los datos siguen disponibles durante 60 días por si quieres volver." },
  { q: "¿Puedo cambiar de plan después?",
    a: "Sí, en cualquier momento desde Plan & Billing en tu panel. Si subes de plan se prorratea inmediatamente; si bajas, el cambio se aplica al inicio del siguiente ciclo." },
  { q: "¿Cancelar tiene permanencia?",
    a: "Cero permanencia. Cancela cuando quieras desde el panel y mantienes acceso hasta el final del periodo ya pagado." },
  { q: "¿Cuál es la diferencia entre Agenda & Gestión y Ecosystem Full?",
    a: "Agenda & Gestión cubre reservas, marketplace y facturación básica · ideal para un autónomo que empieza. Ecosystem Full añade equipo, AEAT automático, IRPF split, justificantes con OCR, IA predictiva y posicionamiento premium en el marketplace." },
  { q: "¿Los precios incluyen IVA?",
    a: "No. Los precios que ves en esta página son sin IVA. En la facturación se añade el IVA correspondiente a tu país (21 % en España) y recibes factura mensual para deducirlo como gasto." },
  { q: "¿Qué método de pago aceptáis?",
    a: "Tarjeta (débito o crédito) a través de Stripe. Facturación mensual recurrente, cargos automáticos y factura enviada por email cada mes." },
];

export default function Pricing() {
  const { theme, toggle: toggleTheme } = useTheme();
  const navigate = useNavigate();
  const isDark = theme !== "light";
  const [openFaq, setOpenFaq] = useState(0);

  return (
    <main className="min-h-screen" style={{ backgroundColor: isDark ? "#0F1117" : "#FAFAF7", color: isDark ? "#E5E5E5" : "#1A1A1A" }} data-testid="pricing-page">
      {/* Header simple inline (para evitar tocar GlobalHeader) */}
      <header className="border-b" style={{ borderColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-3">
          <Link to="/home" className="flex items-center gap-2 shrink-0 hover:opacity-80 transition-opacity" data-testid="pricing-brand">
            <AutoGlowBrand size={28} />
          </Link>
          <nav className="hidden md:flex items-center gap-7 text-sm">
            <Link to="/marketplace" className="font-medium opacity-70 hover:opacity-100 transition" data-testid="pricing-nav-marketplace">Marketplace</Link>
            <Link to="/pricing" className="font-medium" style={{ color: "var(--ag-gold)" }}>Precios</Link>
            <Link to="/ayuda" className="font-medium opacity-70 hover:opacity-100 transition">Ayuda</Link>
          </nav>
          <div className="flex items-center gap-2">
            <button onClick={toggleTheme} aria-label="Tema"
              className="w-9 h-9 rounded-full flex items-center justify-center transition"
              style={{ backgroundColor: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)" }}
              data-testid="pricing-theme-toggle">
              {isDark ? <Sun size={16} weight="fill" style={{ color: "var(--ag-gold)" }} /> : <Moon size={16} weight="fill" />}
            </button>
            <Link to="/login" className="hidden sm:inline text-sm font-medium opacity-70 hover:opacity-100 transition px-3">Acceder</Link>
            <button onClick={() => navigate("/register")} data-testid="pricing-cta-register"
              className="ag-btn-gold h-10 px-5 text-sm font-bold rounded-full">
              Probar gratis
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="border-b" style={{ borderColor: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)" }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-[0.25em]"
               style={{
                 backgroundColor: isDark ? "rgba(212,175,55,0.10)" : "rgba(212,175,55,0.12)",
                 color: "var(--ag-gold)",
                 border: `1px solid ${isDark ? "rgba(212,175,55,0.20)" : "rgba(212,175,55,0.25)"}`,
               }}>
            <PriceTag size={11} weight="fill" /> Planes profesionales
          </div>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter mt-6 leading-[1.05]" data-testid="pricing-hero-title">
            Un precio simple por taller.
            <br />
            <span style={{ color: "var(--ag-gold)" }}>Sin sorpresas.</span>
          </h1>
          <p className="text-sm sm:text-base opacity-70 mt-5 max-w-2xl mx-auto leading-relaxed">
            30 días de prueba gratis · sin tarjeta · cancela cuando quieras. Si tu negocio crece, AutoGlow crece contigo.
          </p>
        </div>
      </section>

      {/* Cards de precios */}
      <section style={{ backgroundColor: isDark ? "rgba(255,255,255,0.015)" : "rgba(0,0,0,0.02)" }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
          <div className="grid md:grid-cols-2 gap-6 lg:gap-8 items-start max-w-4xl mx-auto">
            {PLANS.map((p, idx) => (
              <PlanCard key={p.id} plan={p} isDark={isDark} elevated={p.popular} />
            ))}
          </div>
          <p className="text-center text-xs opacity-50 mt-8">Precios sin IVA. Facturación mensual recurrente vía Stripe. Cancela en 1 click.</p>
        </div>
      </section>

      {/* Tabla comparativa */}
      <section className="border-t" style={{ borderColor: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)" }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
          <div className="text-center">
            <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tighter leading-tight">Compara todos los planes</h2>
            <p className="text-sm opacity-70 mt-3">Todas las funcionalidades clave una al lado de la otra. Sin asteriscos ni letra pequeña.</p>
          </div>
          <div className="mt-10 rounded-2xl border overflow-hidden overflow-x-auto"
               style={{
                 borderColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)",
                 backgroundColor: isDark ? "rgba(255,255,255,0.02)" : "#FFFFFF",
               }} data-testid="pricing-comparison-table">
            <table className="w-full min-w-[640px]">
              <thead>
                <tr style={{ borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"}` }}>
                  <th className="text-left px-5 py-4 text-[10px] font-bold uppercase tracking-[0.18em] opacity-50">Característica</th>
                  <th className="text-center px-5 py-4 text-[10px] font-bold uppercase tracking-[0.18em] opacity-70">Agenda & Gestión</th>
                  <th className="text-center px-5 py-4" style={{ backgroundColor: isDark ? "rgba(212,175,55,0.08)" : "rgba(212,175,55,0.07)" }}>
                    <span className="text-[10px] font-bold uppercase tracking-[0.18em]" style={{ color: "var(--ag-gold)" }}>Ecosystem Full</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {COMPARISON_ROWS.map((row, ri) => (
                  <tr key={ri} style={{ borderBottom: ri === COMPARISON_ROWS.length - 1 ? "none" : `1px solid ${isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)"}` }}>
                    <td className="px-5 py-3.5 text-sm font-medium">{row[0]}</td>
                    {row.slice(1).map((cell, ci) => {
                      const middleCol = ci === 1;
                      return (
                        <td key={ci} className="px-5 py-3.5 text-center text-sm"
                            style={{ backgroundColor: middleCol ? (isDark ? "rgba(212,175,55,0.05)" : "rgba(212,175,55,0.04)") : "transparent" }}>                          {cell === "yes"
                            ? <Check size={16} weight="bold" className="mx-auto" style={{ color: "var(--ag-gold)" }} />
                            : cell === "no"
                              ? <span className="opacity-30">—</span>
                              : <span className={middleCol ? "font-semibold" : ""} style={middleCol ? { color: "var(--ag-gold)" } : {}}>{cell}</span>}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t" style={{ borderColor: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)" }}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
          <div className="text-center">
            <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tighter leading-tight">Preguntas frecuentes</h2>
            <p className="text-sm opacity-70 mt-3">Las dudas que más nos hacen antes de empezar.</p>
          </div>
          <div className="mt-10 space-y-2.5" data-testid="pricing-faq">
            {FAQS.map((f, i) => {
              const open = openFaq === i;
              return (
                <div key={i} className="rounded-xl border overflow-hidden transition-all"
                     style={{
                       borderColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)",
                       backgroundColor: open ? (isDark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.015)") : "transparent",
                     }}>
                  <button onClick={() => setOpenFaq(open ? -1 : i)}
                          className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left"
                          data-testid={`faq-q-${i}`}>
                    <span className="font-semibold text-sm sm:text-base pr-4">{f.q}</span>
                    <CaretDown size={16} weight="bold" className="opacity-50 shrink-0 transition-transform" style={{ transform: open ? "rotate(180deg)" : "rotate(0)" }} />
                  </button>
                  {open && (
                    <div className="px-5 pb-4 text-sm opacity-70 leading-relaxed" data-testid={`faq-a-${i}`}>{f.a}</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA banda dorada */}
      <section style={{ background: "linear-gradient(135deg, #D4AF37 0%, #C59B2E 50%, #B8860B 100%)" }}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20 text-center text-[#1A1A1A]">
          <Sparkle size={32} weight="fill" className="mx-auto mb-4 opacity-70" />
          <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tighter leading-tight">¿Listo para que las reservas vengan solas?</h2>
          <p className="text-sm sm:text-base mt-3 opacity-80">Empieza gratis hoy. En 30 días tendrás más reservas, menos huecos vacíos y la contabilidad lista para tu gestor.</p>
          <button onClick={() => navigate("/register")} data-testid="pricing-cta-bottom"
            className="inline-flex items-center gap-2 mt-8 px-8 h-12 rounded-full bg-[#0F1117] text-[#FAFAF7] font-bold text-sm hover:scale-[1.02] active:scale-[0.98] transition shadow-lg">
            Probar AutoGlow 30 días gratis <ArrowRight size={16} weight="bold" />
          </button>
          <p className="text-[11px] opacity-60 mt-4">Sin tarjeta · Sin permanencia · Cancela cuando quieras</p>
        </div>
      </section>

      {/* Footer simple */}
      <footer className="border-t" style={{ borderColor: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs opacity-60">
          <div className="flex items-center gap-3"><AutoGlowBrand size={20} /> <span>© {new Date().getFullYear()} AutoGlow Ecosystem</span></div>
          <div className="flex gap-6">
            <Link to="/marketplace" className="hover:opacity-100 transition">Marketplace</Link>
            <Link to="/login" className="hover:opacity-100 transition">Acceder</Link>
            <Link to="/register" className="hover:opacity-100 transition">Registro</Link>
          </div>
        </div>
      </footer>
    </main>
  );
}

function PlanCard({ plan, isDark, elevated }) {
  const navigate = useNavigate();
  const isGold = plan.style === "gold";
  const handleCta = () => {
    if (plan.ctaHref.startsWith("mailto:")) { window.location.href = plan.ctaHref; }
    else navigate(plan.ctaHref);
  };
  return (
    <div className={`relative ${elevated ? "" : "md:pt-12"}`} data-testid={`plan-card-${plan.id}`}>
      <div className="rounded-2xl border h-full flex flex-col transition-all duration-300 hover:-translate-y-1"
           style={{
             borderColor: isGold ? "rgba(212,175,55,0.6)" : (isDark ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.08)"),
             backgroundColor: isGold ? (isDark ? "rgba(212,175,55,0.04)" : "#FFFFFF") : (isDark ? "rgba(255,255,255,0.02)" : "#FFFFFF"),
             boxShadow: isGold ? "0 20px 60px -15px rgba(212,175,55,0.25)" : "none",
           }}>
        {plan.popular && (
          <div className="absolute -top-3 inset-x-0 flex justify-center">
            <span className="inline-flex items-center gap-1.5 px-4 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.18em] shadow-md"
                  style={{ background: "linear-gradient(135deg, #D4AF37 0%, #B8860B 100%)", color: "#1A1A1A" }}>
              <Star size={11} weight="fill" /> Más popular
            </span>
          </div>
        )}
        <div className="p-7 flex flex-col flex-1">
          <h3 className="font-display font-bold text-lg tracking-tight" style={{ color: isGold ? "var(--ag-gold)" : (isDark ? "#FAFAF7" : "#1A1A1A") }}>{plan.name}</h3>
          <p className="text-sm opacity-65 mt-1 leading-relaxed">{plan.desc}</p>
          <div className="mt-6 flex items-baseline gap-1.5">
            {plan.price !== null ? (
              <>
                <span className="font-display font-black text-5xl tracking-tighter" style={isGold ? { color: "var(--ag-gold)" } : {}}>{plan.price}€</span>
                <span className="text-sm opacity-60">{plan.period}</span>
              </>
            ) : (
              <span className="font-display font-black text-3xl tracking-tighter" style={{ color: "var(--ag-gold)" }}>{plan.period}</span>
            )}
          </div>
          {plan.price !== null && (
            <div className="text-[11px] uppercase tracking-wider opacity-55 mt-1" data-testid={`plan-vat-${plan.id}`}>
              +21% IVA · facturación mensual
            </div>
          )}
          <button onClick={handleCta} data-testid={`plan-cta-${plan.id}`}
            className={`mt-6 w-full h-11 rounded-full text-sm font-bold flex items-center justify-center gap-2 transition active:scale-[0.98] ${
              isGold
                ? "ag-btn-gold shadow-md hover:shadow-lg"
                : ""
            }`}
            style={isGold ? {} : {
              backgroundColor: isDark ? "#FAFAF7" : "#1A1A1A",
              color: isDark ? "#1A1A1A" : "#FAFAF7",
            }}>
            {plan.cta} <ArrowRight size={14} weight="bold" />
          </button>

          <div className="mt-7 border-t pt-6" style={{ borderColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)" }}>
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] opacity-50 mb-4">Qué incluye</p>
            <ul className="space-y-3">
              {plan.features.map((f, i) => (
                <li key={i} className="flex items-start gap-2.5 text-sm leading-relaxed">
                  <Check size={15} weight="bold" className="mt-0.5 shrink-0" style={{ color: "var(--ag-gold)" }} />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
